// Places (locations v2, slice 6): a tree per location type. The API gives a
// flat list, depth first, each place with its path from the root
// ([{id, name, is_unsorted}]); items carry location_id and location_path.
import { useCallback, useEffect, useState } from 'react';
import api from './config/api';
import { errorMessage } from './errors';

export const PATH_SEPARATOR = ' › ';

/** A place's name as shown: the Unsorted root is stored in English and translated. */
export const placeName = (t, place) => (place && place.is_unsorted ? t('locations.unsorted') : (place?.name ?? ''));

/** "Salon › Armoire › Étagère 2" */
export const pathLabel = (t, path) => (path || []).map(segment => placeName(t, segment)).join(PATH_SEPARATOR);

/** Folded like the server's search text: case, accents and spacing ignored. */
export const fold = (text) => String(text || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();

/** Every word of the query somewhere in the place's path. */
export const pathMatches = (t, place, query) => {
  const words = fold(query).split(' ').filter(Boolean);
  if (!words.length) return true;
  const label = fold(pathLabel(t, place.path));
  return words.every(word => label.includes(word));
};

/** The places of a location type, fetched (and fetched again with reload()). */
export function useLocations(locationTypeId) {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(!!locationTypeId);
  const [error, setError] = useState(null);

  const reload = useCallback(() => {
    if (!locationTypeId) {
      setPlaces([]);
      setLoading(false);
      return Promise.resolve([]);
    }
    setLoading(true);
    return api.get(`/location-types/${locationTypeId}/locations`)
      .then(response => {
        setPlaces(response.data || []);
        setError(null);
        return response.data || [];
      })
      .catch(err => {
        setError(err.response?.data?.error || err.message);
        return [];
      })
      .finally(() => setLoading(false));
  }, [locationTypeId]);

  useEffect(() => { reload(); }, [reload]);

  return { places, loading, error, reload };
}

/** The message for a refused tree change (by the API's code). */
export const locationErrorMessage = (t, error) => {
  const code = error?.response?.data?.code;
  if (code && t(`locations.errors.${code}`, { defaultValue: '' })) {
    return t(`locations.errors.${code}`, error.response.data);
  }
  return t('locations.errors.generic', { error: errorMessage(t, error) });
};
